import type { Transaction } from '@tiptap/pm/state';
import { NODE_SEARCH_TARGETS } from './node-search-targets';
import type { SearchResult } from './search';

export interface ReplaceOptions {
    searchTerm: string;
    replaceTerm: string;
    results: SearchResult[];
    resultIndex: number;
    regex: boolean;
    caseSensitive: boolean;
    wholeWord: boolean;
}

export const getRegex = (s: string, disableRegex: boolean, caseSensitive: boolean, wholeWord: boolean): RegExp | null => {
    try {
        let pattern = disableRegex ? s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : s;
        if (wholeWord) pattern = `\\b(?:${pattern})\\b`;
        return RegExp(pattern, caseSensitive ? 'gu' : 'gui');
    } catch {
        return null;
    }
};

function replaceFirstMatchInValue(value: string, re: RegExp, replaceTerm: string, occurrenceIndex = 0): string | null {
    const matches = Array.from(value.matchAll(re)).filter(([m]) => m.trim());
    const match = matches[occurrenceIndex];
    if (!match || match.index === undefined) return null;
    const start = match.index;
    const end = start + match[0].length;
    return value.slice(0, start) + replaceTerm + value.slice(end);
}

function getMatchOccurrenceInNode(results: SearchResult[], resultIndex: number): number {
    const current = results[resultIndex];
    let occurrence = 0;
    for (let i = 0; i < resultIndex; i += 1) {
        const r = results[i];
        if (r.from === current.from && r.nodeType === current.nodeType) occurrence += 1;
    }
    return occurrence;
}

function computeOccurrenceIndices(results: SearchResult[]): number[] {
    const counts = new Map<string, number>();
    const out = new Array<number>(results.length);
    for (let resultIndex = 0; resultIndex < results.length; resultIndex += 1) {
        const result = results[resultIndex];
        if (result.nodeType === 'text') {
            out[resultIndex] = 0;
            continue;
        }
        const key = `${result.from}|${result.nodeType}`;
        const count = counts.get(key) ?? 0;
        out[resultIndex] = count;
        counts.set(key, count + 1);
    }
    return out;
}

const isReplaceableResult = (result: SearchResult): boolean => {
    return (
        NODE_SEARCH_TARGETS.get(result.nodeType)?.attr_and_replaceable.find(
            attr_and_replaceable => attr_and_replaceable.attr === result.matchedAttr,
        )?.replaceable ?? false
    );
};

function applyReplaceToResult(
    tr: Transaction,
    result: SearchResult,
    replaceTerm: string,
    re: RegExp | null,
    occurrenceIndex: number,
): boolean {
    if (result.nodeType === 'text') {
        tr.insertText(replaceTerm, result.from, result.to);
        return true;
    }

    if (!isReplaceableResult(result) || !re) return false;

    const node = tr.doc.nodeAt(result.from);
    if (!node) return false;

    const matchedAttr = result.matchedAttr;
    if (!matchedAttr) return false;
    const value = node.attrs[matchedAttr];
    if (typeof value !== 'string') return false;

    const newValue = replaceFirstMatchInValue(value, re, replaceTerm, occurrenceIndex);
    if (newValue === null) return false;

    tr.setNodeMarkup(result.from, node.type, { ...node.attrs, [matchedAttr]: newValue });
    return true;
}

export function applyReplace(tr: Transaction, options: ReplaceOptions): boolean {
    const { searchTerm, replaceTerm, results, resultIndex, regex, caseSensitive, wholeWord } = options;
    const result = results[resultIndex];
    if (!result) return false;

    const re = result.nodeType === 'text' ? null : getRegex(searchTerm, !regex, caseSensitive, wholeWord);
    const occurrenceIndex = result.nodeType === 'text' ? 0 : getMatchOccurrenceInNode(results, resultIndex);

    return applyReplaceToResult(tr, result, replaceTerm, re, occurrenceIndex);
}

export function applyReplaceAll(tr: Transaction, options: ReplaceOptions): boolean {
    const { searchTerm, replaceTerm, results, regex, caseSensitive, wholeWord } = options;
    if (results.length === 0) return false;

    const re = getRegex(searchTerm, !regex, caseSensitive, wholeWord);
    const occurrenceIndices = computeOccurrenceIndices(results);

    const indices = results.map((_, i) => i);
    indices.sort((a, b) => {
        const byFrom = results[b].from - results[a].from;
        return byFrom !== 0 ? byFrom : b - a;
    });

    let replaced = false;
    for (const i of indices) {
        if (applyReplaceToResult(tr, results[i], replaceTerm, re, occurrenceIndices[i])) {
            replaced = true;
        }
    }
    return replaced;
}
